import { useSnackbar } from "notistack";
import { useTranslation } from "react-i18next";
import { useQuery } from "react-query";
import { useSearchParams } from "react-router-dom";
import { FilterTag, FrontendFilterTag, LayerSet } from "types/types";
import { convertStringArrToNumberArr } from "utils/ArrayUtils";
import { get } from "utils/AxiosUtils";

import { deserializeErrors } from "utils/ErrorUtils";
import { makeTree } from "utils/Tree";

export function useFilterTags() {
  const { enqueueSnackbar } = useSnackbar();
  async function fetch(): Promise<{
    originalTags: FilterTag[];
    treeTags: FrontendFilterTag[];
  }> {
    const { data } = await get<FilterTag[]>(`/api/tags`);
    const tree = makeTree(data);
    // const ok = buildTree(filterTags, "id", "parentId");

    return { originalTags: data, treeTags: tree };
  }
  return useQuery(["tags"], fetch, {
    onError: (err) => {
      const errors = deserializeErrors(err);
      errors.map((er) => {
        return enqueueSnackbar(er.message, {
          variant: "error",
          preventDuplicate: true,
        });
      });
    },
    cacheTime: Infinity,
    staleTime: Infinity,
  });
}

export function useUrlFilterTags() {
  const [params, setParams] = useSearchParams();
  const tags = params.get("tags");
  const splitTags = tags ? tags.split(",") : [];
  const tagIds = convertStringArrToNumberArr(splitTags);

  function updateTags(tagId: number, removeFilterKey: boolean = false) {
    const newParams = new URLSearchParams(params);
    const isActiveTag = tagIds.includes(tagId);

    if (removeFilterKey) {
      newParams.delete("tkey");
    }

    if (isActiveTag) {
      newParams.set("tags", tagIds.filter((e) => e !== tagId).join(","));
    } else {
      newParams.set("tags", [...tagIds, tagId].join(","));
    }
    // When filter change we want to reset the page to 1
    newParams.delete("page");
    setParams(newParams);
  }

  function resetTagFilters() {
    const newParams = new URLSearchParams(params);

    newParams.delete("tags");
    newParams.delete("search");

    // When filter change we want to reset the page to 1
    newParams.delete("page");
    setParams(newParams);
  }

  return { tagIds, updateTags, resetTagFilters };
}

export function useUrlSearch() {
  const [params, setParams] = useSearchParams();
  const search = params.get("search") || "";

  function updateSearch(search: string) {
    const newParams = new URLSearchParams(params);
    newParams.set("search", search);
    // When filter change we want to reset the page to 1
    newParams.delete("page");
    setParams(newParams);
  }

  return { search, updateSearch };
}

export function useSortOptions() {
  const { t } = useTranslation();
  const sortOptions = [
    { value: "asc", label: t("label.asc") },
    { value: "desc", label: t("label.desc") },
  ];
  return sortOptions;
}

export function useUrlSort() {
  const [params, setParams] = useSearchParams();
  const sortOptions = useSortOptions();
  const sortOption = params.get("sort");

  const [firstSortOption] = sortOptions;
  const selectedSortOption =
    sortOptions.find((option) => option.value === sortOption) ||
    firstSortOption;

  function updateSort(sortOptionValue: string) {
    const newParams = new URLSearchParams(params);
    newParams.set("sort", sortOptionValue);
    // When filter change we want to reset the page to 1
    newParams.delete("page");
    setParams(newParams);
  }

  return { selectedSortOption, updateSort };
}

export function usePagination() {
  const [params, setParams] = useSearchParams();
  const page = +(params.get("page") || "1");
  const limit = +(params.get("limit") || "10");

  function updatePage(page: number) {
    const newParams = new URLSearchParams(params);
    newParams.set("page", page.toString());
    setParams(newParams);
  }

  function updateLimit(limit: number) {
    const newParams = new URLSearchParams(params);
    newParams.set("limit", limit.toString());
    setParams(newParams);
  }

  function paginate(array: LayerSet[]) {
    return array.slice((page - 1) * limit, page * limit);
  }

  return { page, limit, updatePage, updateLimit, paginate };
}

export function useUrlFilterTagKeys() {
  const [params] = useSearchParams();
  const tagKey = params.get("tkey");

  return { tagKey };
}
